import {
  logEvent,
  setUserProperties,
  type Analytics,
} from 'firebase/analytics';
import {
  initFirebaseAnalytics,
  setAnalyticsCollection,
} from '@/shared/lib/firebase';

/**
 * Funnel analytics — a thin, typed layer over Firebase / Google Analytics.
 *
 * WHY THIS EXISTS: the most common reasons a user uploads an angle but never
 * renders happen entirely client-side (subscription/token/queue paywalls,
 * empty-refine disabled button) and abort BEFORE any request reaches the API,
 * so the server is blind to them. This records the user's intent (the Generate
 * click) and the reason each render attempt is blocked, so the drop-off is
 * observable.
 *
 * CROSS-PLATFORM CONTRACT: the event/param/user-property names below are the
 * shared schema — they MUST stay identical to Apple
 * (`apps/apple/.../AnalyticsManager.swift`) and Android
 * (`apps/android/.../AnalyticsTracker.kt`). Renaming an event after data starts
 * flowing breaks funnel comparability across platforms, so treat these strings
 * as load-bearing.
 *
 * No-ops outside production (and in unsupported/SSR contexts) because
 * `initFirebaseAnalytics()` resolves to `null` there — see firebase.ts.
 */

export const AnalyticsEvent = {
  /**
   * Early-funnel visibility (registration → onboarding → first upload). These
   * fire BEFORE `angle_upload_completed` so we can tell "wrong product / left on
   * onboarding" from "hidden bug blocking first upload". Event/param strings are
   * a byte-identical cross-platform contract with Apple/Android — do not rename.
   */
  ONBOARDING_STARTED: 'onboarding_started',
  ONBOARDING_SLIDE_VIEWED: 'onboarding_slide_viewed',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  ONBOARDING_DISMISSED: 'onboarding_dismissed',
  /** Upload INTENT — the counterpart to `angle_upload_completed` for measuring
   *  intent→completion drop-off. */
  ANGLE_UPLOAD_STARTED: 'angle_upload_started',
  /** A real upload/processing failure (NOT a plain OS-picker cancel). */
  ANGLE_UPLOAD_FAILED: 'angle_upload_failed',
  /** The empty buildings/projects home (no projects yet) is shown. */
  HOME_EMPTY_STATE_VIEWED: 'home_empty_state_viewed',
  ANGLE_UPLOAD_COMPLETED: 'angle_upload_completed',
  RENDER_SHEET_OPENED: 'render_sheet_opened',
  /** Fired FIRST in the Generate handler, before any gate — captures intent. */
  GENERATE_TAPPED: 'generate_tapped',
  /** Fired in each preflight gate that aborts before the POST. */
  GENERATE_BLOCKED: 'generate_blocked',
  RENDER_CREATED: 'render_created',
  /** A render reached `completed` (upscales and intermediate renders excluded)
   *  — fired from the realtime completion handler, deduped per render id. */
  RENDER_COMPLETED: 'render_completed',
  /**
   * Once per browser profile (localStorage flag): the very first completed
   * render. UAC conversion-import target — do NOT rename.
   */
  FIRST_RENDER_COMPLETED: 'first_render_completed',
  /**
   * Once per browser profile (localStorage flag): the very first successfully
   * uploaded angle, any angle_kind. Measures the registered→activated gap
   * (~35% of registered users never upload). Do NOT rename.
   */
  FIRST_ANGLE_UPLOADED: 'first_angle_uploaded',
  RENDER_ERROR: 'render_error',
  /** Successful NEW-account registration (see AuthMethod). On OAuth flows the
   *  wire carries no new-vs-existing flag, so OAuth logs `login` only. */
  SIGN_UP: 'sign_up',
  /** Successful login (email/password or OAuth callback). */
  LOGIN: 'login',
  /** Estimate/album generation funnel — `*_requested` at the user-triggered
   *  API call, `*_generated` at the realtime completion (deduped per id). */
  ESTIMATE_REQUESTED: 'estimate_requested',
  ESTIMATE_GENERATED: 'estimate_generated',
  ALBUM_REQUESTED: 'album_requested',
  ALBUM_GENERATED: 'album_generated',
  /** The token packages storefront became visible (Android parity:
   *  TokenPackagesViewModel). Params: trigger, tokens_balance. */
  TOKEN_PAYWALL_VIEW: 'token_paywall_view',
  /**
   * Facade Pack conversion funnel (Facade Pack pivot). WEB is read-only for
   * purchases — it logs the SHOWN events for every pack surface (pack /
   * pro_plan), plus `pack_purchase_started` when the user taps a "get in the
   * app" store badge (the WEB analog of starting a purchase — the actual
   * checkout + completion happen on mobile, so WEB never logs `*_completed`).
   * `surface` / `trigger` / `project_bound` params and the event names are an
   * identical cross-platform contract.
   */
  PACK_PAYWALL_SHOWN: 'pack_paywall_shown',
  PACK_PURCHASE_STARTED: 'pack_purchase_started',
  PRO_PLAN_SUBSCRIBE_STARTED: 'pro_plan_subscribe_started',
  /**
   * Token checkout reached the payment overlay — fired at the tap, mirroring
   * Apple's `tokenPurchaseStarted` and Android's `TOKEN_PURCHASE_STARTED`.
   * Without it everything between "picked a package" and "tokens landed" was
   * one unmeasured jump. Param: `package_slug`.
   */
  TOKEN_PURCHASE_STARTED: 'token_purchase_started',
  /**
   * A checkout ended WITHOUT a sale, on any money path. One event for every
   * product (`product_kind` splits them) rather than a parallel
   * `*_purchase_failed` per product: the outcome vocabulary is shared, so
   * "how many people tried to pay and could not, and why" is one query.
   * `outcome` carries the shared PurchaseCheckoutOutcome, `reason` the
   * provider's own code. Cross-platform contract — identical to Apple's
   * `purchase_failed` and Android's `PURCHASE_FAILED`.
   */
  PURCHASE_FAILED: 'purchase_failed',
} as const;
export type AnalyticsEvent =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/** Which Facade Pack surface a `pack_paywall_shown` event came from. Shared
 *  vocabulary — keep identical to Apple/Android. */
export const PaywallSurface = {
  PACK: 'pack',
  PRO_PLAN: 'pro_plan',
} as const;
export type PaywallSurface =
  (typeof PaywallSurface)[keyof typeof PaywallSurface];

/** What surfaced a Facade Pack paywall. Coarse trigger derived from the server
 *  payload's primary-action kind (the limit code is not carried in the
 *  payload), or "storefront" when opened from the plans page. */
export const PackTrigger = {
  PURCHASE_PACK: 'purchase_pack',
  PRO_PLAN: 'pro_plan',
  STOREFRONT: 'storefront',
} as const;
export type PackTrigger = (typeof PackTrigger)[keyof typeof PackTrigger];

/**
 * Why a checkout ended. Mirrors the API `PurchaseIntentOutcomeEnum`, Apple's
 * `PurchaseCheckoutOutcome` and Android's `PurchaseCheckoutOutcome`.
 *
 * `granted` is deliberately absent: only the server may declare a sale, from
 * inside the grant transaction. A client able to report success could poison
 * every funnel metric and alert built on this data.
 */
export const PurchaseCheckoutOutcome = {
  /** The buyer closed the checkout overlay. Normal, not a fault. */
  USER_CANCELLED: 'user_cancelled',
  /** The provider refused the charge (region, declined card, no method). */
  STORE_REFUSED: 'store_refused',
  /** The product never loaded, so no checkout was shown. */
  PRODUCT_UNAVAILABLE: 'product_unavailable',
  /** Accepted but not final, or charged with our verify still pending. */
  PENDING_EXTERNAL: 'pending_external',
  /** Charged and our server refused it. Paid and got nothing. */
  VERIFY_FAILED: 'verify_failed',
  /** We broke before or after the provider round-trip. */
  CLIENT_ERROR: 'client_error',
} as const;
export type PurchaseCheckoutOutcome =
  (typeof PurchaseCheckoutOutcome)[keyof typeof PurchaseCheckoutOutcome];

/** Which product a checkout was for. Keeps one event usable across all paths. */
export const PurchaseProductKind = {
  PACK: 'pack',
  TOKENS: 'tokens',
  PRO_PLAN: 'pro_plan',
  PASS: 'pass',
  SUBSCRIPTION: 'subscription',
} as const;
export type PurchaseProductKind =
  (typeof PurchaseProductKind)[keyof typeof PurchaseProductKind];

/** Shared vocabulary for why a render attempt did not reach the API. */
export const BlockReason = {
  SUBSCRIPTION_REQUIRED: 'subscription_required',
  INSUFFICIENT_TOKENS: 'insufficient_tokens',
  // Free-tier designs allowance spent (Facade Pack pivot) — value mirrors the
  // server queue-status block_reason; cross-platform analytics contract.
  FREE_DESIGNS_EXHAUSTED: 'free_designs_exhausted',
  QUEUE_FULL: 'queue_full',
  EMPTY_REFINE: 'empty_refine',
  GOALS_FAILED: 'goals_failed',
  SETTINGS_LOADING: 'settings_loading',
} as const;
export type BlockReason = (typeof BlockReason)[keyof typeof BlockReason];

/** Shared vocabulary for render_error after the POST left the device. */
export const ErrorType = {
  NETWORK: 'network',
  OFFLINE: 'offline',
  SERVER: 'server',
  VALIDATION: 'validation',
  UNKNOWN: 'unknown',
} as const;
export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

/** Auth method vocabulary for `sign_up` / `login` — cross-platform contract. */
export const AuthMethod = {
  EMAIL: 'email',
  GOOGLE: 'google',
  APPLE: 'apple',
} as const;
export type AuthMethod = (typeof AuthMethod)[keyof typeof AuthMethod];

type EventParams = Record<string, string | number | boolean>;

/**
 * Once-per-browser-profile flags (localStorage). `first_render_completed` is a
 * UAC conversion-import target; `first_angle_uploaded` measures activation.
 * Keys are storage-local (not part of the cross-platform event contract).
 */
export const FirstTimeFlag = {
  RENDER_COMPLETED: 'analytics.first_render_completed',
  ANGLE_UPLOADED: 'analytics.first_angle_uploaded',
} as const;
export type FirstTimeFlag = (typeof FirstTimeFlag)[keyof typeof FirstTimeFlag];

/**
 * True exactly once per browser profile per flag — marks the flag on the first
 * call. Fail-closed (returns false) on SSR or blocked storage so a broken
 * environment can never inflate the once-only conversion events.
 */
/** Non-consuming peek at a first-time flag (same fail-closed semantics). */
export function isFirstTimeFlagUnset(flag: FirstTimeFlag): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(flag) === null;
  } catch {
    return false;
  }
}

export function consumeFirstTimeFlag(flag: FirstTimeFlag): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (window.localStorage.getItem(flag) !== null) return false;
    window.localStorage.setItem(flag, '1');
    return true;
  } catch {
    return false;
  }
}

/** Per-event sets of already-tracked entity ids (session-scoped). */
const trackedEntityIds = new Map<AnalyticsEvent, Set<string>>();

/**
 * Track an entity-completion event at most once per entity id for this
 * session. Realtime completion events can replay (WS redelivery, reconnect
 * reconcile, multi-handler races) — this is the shared double-fire guard for
 * render/album/estimate `*_completed`/`*_generated`. Returns whether the
 * event fired (false = already tracked).
 */
export function trackEventOnceForId(
  event: AnalyticsEvent,
  id: string,
  params: EventParams = {}
): boolean {
  let ids = trackedEntityIds.get(event);
  if (!ids) {
    ids = new Set();
    trackedEntityIds.set(event, ids);
  }
  if (ids.has(id)) return false;
  ids.add(id);
  trackEvent(event, params);
  return true;
}

function withAnalytics(fn: (analytics: Analytics) => void): void {
  void initFirebaseAnalytics().then((analytics) => {
    if (analytics) fn(analytics);
  });
}

export function trackEvent(
  event: AnalyticsEvent,
  params: EventParams = {}
): void {
  // `as string` routes through logEvent's custom-event overload: `login` /
  // `sign_up` are GA reserved names with stricter typed signatures, but our
  // params already match the recommended shape ({ method }).
  withAnalytics((analytics) => logEvent(analytics, event as string, params));
}

export interface AnalyticsIdentity {
  planSlug?: string | null;
  planType?: string | null;
  /** Passes are mobile-only — always false on web (no purchase flow here). */
  hasProPass?: boolean;
  appLocale: string;
}

/**
 * Set the slowly-changing funnel dimensions. Call wherever the profile / plan
 * (re)loads. `platform` is always `web`; passes are always false here.
 */
export function setAnalyticsUser(identity: AnalyticsIdentity): void {
  withAnalytics((analytics) => {
    setUserProperties(analytics, {
      plan_slug: identity.planSlug ?? '',
      plan_type: identity.planType ?? '',
      has_pro_pass: String(identity.hasProPass ?? false),
      app_locale: identity.appLocale,
      platform: 'web',
    });
  });
}

/**
 * Keep internal QA/team (`is_test`) traffic out of the GA property. Call
 * wherever the profile (re)loads, so a flag flipped server-side takes effect
 * on the next refresh. Set unconditionally (not only when disabling): a
 * previous test session must re-enable collection when a non-test user signs
 * in on the same browser, otherwise the off state would stick.
 */
export function setAnalyticsCollectionForUser(isTest: boolean): void {
  void setAnalyticsCollection(!isTest);
}
